import { useQuery } from "@tanstack/react-query";
import { get, patch, post, apiDelete } from "./divertidogsApiUtils";
import { String } from "aws-sdk/clients/cloudsearch";
import { objectToFormData } from "../utils/formDataUtils";
import { RespuestaArchivo } from "../utils/fileUtils";
import { RespuestaLeerPropietarios } from "./propietariosServicios";

export interface ListaClientes {
  id: string;
  nombre: string;
}

export interface CorreoMasivo {
  grupoPropietarios?: ListaClientes;
  propietariosIndividuales: Array<RespuestaLeerPropietarios>,
  asunto: string;
  cuerpo: string | null;
}

export type NuevoCorreoMasivo = {
  asunto: string;
  cuerpo?: string;
  grupoPropietarios?: string;
  propietariosIndividuales: Array<string>;
  adjunto?: File;
  cuerpoGrafico?: File;
};

export interface PlantillaCorreoMasivo extends CorreoMasivo {
  id: string;
  programacion?: ProgramacionPlantilla;
  tieneAdjunto: boolean;
  cuerpoEsGrafico: boolean;
}

export type PlantillaCorreoMasivoResumida = Omit<
  PlantillaCorreoMasivo,
  "cuerpo"
> & {
  tieneAdjunto: boolean;
};

export interface NuevaPlantillaCorreoMasivo extends NuevoCorreoMasivo {
  periodicidad?: PeriodicidadCorreoMasivo;
}

export const crearPlantilla = async (
  nuevaPlantilla: NuevaPlantillaCorreoMasivo
): Promise<{ id: string }> => {
  const formData = objectToFormData(nuevaPlantilla, true);
  return post("correos_masivos/plantillas", formData);
};

export const enviarCorreo = async (
  nuevoCorreoMasivo: NuevoCorreoMasivo
): Promise<void> => {
  const formData = objectToFormData(nuevoCorreoMasivo, true);
  return post("correos_masivos/enviar", formData);
};

export const enviarPlantilla = async (idPlantilla: String): Promise<void> => {
  return post(`correos_masivos/plantillas/${idPlantilla}/enviar`);
};

export interface ModificacionesPlantilla {
  id: string;
  asunto?: string;
  grupoPropietarios?: string | null;
  propietariosIndividuales?: Array<string>;
  cuerpo?: string | null;
  cuerpoGrafico?: File | null;
  periodicidad?: PeriodicidadCorreoMasivo | null;
  eliminarProgramacion?: boolean;
  eliminarAdjunto?: boolean;
}

export const editarPlantilla = async (
  modificaciones: ModificacionesPlantilla
): Promise<void> => {
  const { id, ...rest } = modificaciones;
  const formData = objectToFormData(rest, true);
  return patch(`correos_masivos/plantillas/${id}`, formData);
};

export interface ProgramacionPlantilla {
  id: string;
  periodicidad: PeriodicidadCorreoMasivo;
  ultimoEnvio: string | null;
}

export type PeriodicidadCorreoMasivo =
  | "DIARIA"
  | "SEMANAL"
  | "QUINCENAL"
  | "MENSUAL"
  | "ANUAL";

export const PERIODICIDADES_CORREOS_MASIVOS: PeriodicidadCorreoMasivo[] = [
  "DIARIA",
  "SEMANAL",
  "MENSUAL",
  "QUINCENAL",
  "ANUAL",
];

export const labelPorPeriodicidadCorreoMasivo: Record<
  PeriodicidadCorreoMasivo,
  string
> = {
  DIARIA: "Diaria",
  SEMANAL: "Semanal",
  QUINCENAL: "Quincenal",
  MENSUAL: "Mensual",
  ANUAL: "Anual",
};

export const obtenerLabelDePeriodicidadCorreoMasivo = (
  periodicidad: PeriodicidadCorreoMasivo
) => {
  return labelPorPeriodicidadCorreoMasivo[periodicidad];
};

export const leerListasClientes = async (): Promise<
  ListaClientes[] | undefined
> => {
  return get("correos_masivos/listas_clientes");
};

export const QUERY_LEER_LISTAS_CLIENTES = "QUERY_LEER_LISTAS_CLIENTES";

export const useLeerListaClientes = () =>
  useQuery(
    [QUERY_LEER_LISTAS_CLIENTES],
    async () => await leerListasClientes()
  );

export const leerPlantillas = async (): Promise<
  PlantillaCorreoMasivoResumida[] | undefined
> => {
  return get("correos_masivos/plantillas");
};

export const QUERY_LEER_PLANTILLAS = "QUERY_LEER_PLANTILLAS";

export const useLeerPlantillas = () =>
  useQuery([QUERY_LEER_PLANTILLAS], async () => await leerPlantillas());

export const leerPlantilla = async (
  idPlantilla: string
): Promise<PlantillaCorreoMasivo | undefined> => {
  return get(`correos_masivos/plantillas/${idPlantilla}`);
};

export const QUERY_LEER_PLANTILLA = "QUERY_LEER_PLANTILLA";

export const useLeerPlantilla = (idPlantilla: string) =>
  useQuery({
    queryKey: [QUERY_LEER_PLANTILLA, idPlantilla],
    queryFn: async () => await leerPlantilla(idPlantilla),
  });

export const descargarAdjuntoPlantilla = (idPlantilla: string) => {
  return get<RespuestaArchivo>(
    `correos_masivos/plantillas/${idPlantilla}/adjunto`
  );
};

export const descargarCuerpoGrafico = (idPlantilla: string) => {
  return get<RespuestaArchivo>(
    `correos_masivos/plantillas/${idPlantilla}/cuerpo_grafico`
  );
};

export const eliminarPlantilla = async (idPlantilla: String): Promise<void> => {
  return apiDelete(`correos_masivos/plantillas/${idPlantilla}`);
};